/**
 * @file: resolveConversationHandle.ts
 *
 * Resolve a pending {@link ConversationIntent} (a known roomId, a contact, or
 * an org member whose room may not exist yet) into a concrete
 * {@link ChatConversationHandle}. This is the ONE place resolution happens — at
 * the entry boundary, before the conversation renders — so the conversation
 * never carries resolution. A `resolved` intent skips this entirely.
 *
 * The lookup is a single `chat/threads/resolve` call (an O(1) keyed lookup
 * server-side) — no thread-list scan, correct regardless of thread count.
 */

import { ChatConversationHandle } from '@euler/app/flows/chat/lib/conversation/handle';
import { ConversationHandleQuery } from '@euler/app/flows/chat/lib/conversation/types';
import { ResolveChatThreadQuery } from '@euler/model/chat';
import { ChatService } from '@euler/services/chat.service';

export async function resolveConversationHandle(
  chatService: ChatService,
  query: ConversationHandleQuery,
): Promise<ChatConversationHandle> {
  // The FE intent kinds map onto the server's resolve query union.
  const serverQuery: ResolveChatThreadQuery =
    query.kind === 'room'
      ? { kind: 'room', roomId: query.roomId }
      : query.kind === 'contact'
      ? { kind: 'peer', peerPlatformUserId: query.peer.platformUserId }
      : { kind: 'member', orgMemberId: query.orgMemberId };

  const { thread, peer } = await chatService.resolveThread(serverQuery);
  if (thread) {
    return {
      kind: 'baked',
      roomId: thread.roomId,
      // The resolve endpoint returns '' when the room has no per-user thread
      // yet; normalise that to null at the boundary so '' never leaks inward.
      threadId: thread.threadId || null,
      roomType: thread.roomType,
      title: thread.title,
    };
  }
  // No room yet → open bare (the first send materialises it). The peer comes
  // from the server for a `member` query (the caller only had a member id), or
  // from the query itself for a `contact`. A roomId that resolves to nothing
  // is a genuinely missing / inaccessible room.
  if (peer) {
    return {
      kind: 'bare',
      peer: {
        platformUserId: peer.platformUserId,
        appId: peer.appId,
        appScope: peer.appScope,
        name: peer.name ?? undefined,
        avatar: peer.avatar,
      },
    };
  }
  if (query.kind === 'contact') return { kind: 'bare', peer: query.peer };
  throw new Error('chat room not found');
}
